import React, { useState, useEffect } from 'react';
import { lambdaService } from '../../services/lambdaService';
import { S3CredentialsStatus } from '../../types/s3Transfer';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Edit2,
  ShieldCheck,
  Key,
  Database,
  FolderOpen,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

export type CloudProvider = 'aws' | 'gcp';

interface S3CredentialsFormProps {
  onCredentialsSaved: (status: S3CredentialsStatus, showToast?: boolean) => void;
  provider?: CloudProvider;
}

export const S3CredentialsForm: React.FC<S3CredentialsFormProps> = ({ onCredentialsSaved, provider = 'aws' }) => {
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [bucketName, setBucketName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentialsStatus, setCredentialsStatus] = useState<S3CredentialsStatus | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    checkCredentialsStatus();
  }, []);

  const isGcp = provider === 'gcp';

  const checkCredentialsStatus = async () => {
    try {
      const status = isGcp
        ? await lambdaService.getGcpCredentialsStatus()
        : await lambdaService.getS3CredentialsStatus();
      if (status.success && status.has_credentials) {
        setCredentialsStatus(status);
        onCredentialsSaved(status, false);
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);

    // Validation
    if (editMode) {
      if (!bucketName.trim()) {
        setError('Please enter a bucket name');
        return;
      }
    } else {
      if (!accessKey.trim() || !secretKey.trim() || !bucketName.trim()) {
        setError('Please fill in all required fields');
        return;
      }
    }

    setLoading(true);

    try {
      const saveFn = isGcp ? lambdaService.saveGcpCredentials.bind(lambdaService) : lambdaService.saveS3Credentials.bind(lambdaService);
      const result = await saveFn(
        editMode ? '' : accessKey.trim(),
        editMode ? '' : secretKey.trim(),
        bucketName.trim(),
        prefix.trim()
      );

      if (result.success) {
        const bucket = (result as any).bucket_name || result.bucketName || bucketName.trim();

        // Clear form
        setAccessKey('');
        setSecretKey('');
        setBucketName('');
        setPrefix('');
        setEditMode(false);

        // Update status
        const status = isGcp
          ? await lambdaService.getGcpCredentialsStatus()
          : await lambdaService.getS3CredentialsStatus();
        if (status.success && status.has_credentials) {
          setCredentialsStatus(status);
          onCredentialsSaved(status, true);
        }

        toast.success(`Connected to bucket: ${bucket}`);
      } else {
        setError(result.error || 'Failed to save credentials');
        toast.error(result.error || 'Failed to save credentials');
      }
    } catch (err) {
      setError(`Error saving credentials: ${err}`);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      const deleteFn = isGcp ? lambdaService.deleteGcpCredentials.bind(lambdaService) : lambdaService.deleteS3Credentials.bind(lambdaService);
      const result = await deleteFn();

      if (result.success) {
        setCredentialsStatus(null);
        setShowDeleteConfirm(false);
        onCredentialsSaved({ success: true, has_credentials: false }, false);
        toast.success('Credentials deleted successfully');
      } else {
        setError(result.error || 'Failed to delete credentials');
        toast.error(result.error || 'Failed to delete credentials');
      }
    } catch (err) {
      setError(`Error deleting credentials: ${err}`);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Checking connection status...</p>
      </div>
    );
  }

  // Connected State
  if (credentialsStatus && credentialsStatus.has_credentials && !editMode) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-full">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{isGcp ? 'GCP Cloud Storage Connected' : 'AWS S3 Connected'}</CardTitle>
                <CardDescription>Your bucket is ready for file transfers</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-green-500/15 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:text-green-400 ring-1 ring-inset ring-green-500/20">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Active
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background/50 p-3 rounded-lg border border-border/50">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Database className="h-3 w-3" /> Bucket Name
              </p>
              <p className="font-medium truncate" title={credentialsStatus.bucket_name || credentialsStatus.bucketName || ''}>
                {credentialsStatus.bucket_name || credentialsStatus.bucketName || 'Unknown'}
              </p>
            </div>
            <div className="bg-background/50 p-3 rounded-lg border border-border/50">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <FolderOpen className="h-3 w-3" /> Prefix
              </p>
              <p className="font-medium truncate">
                {credentialsStatus.prefix || '(Root)'}
              </p>
            </div>
            <div className="bg-background/50 p-3 rounded-lg border border-border/50">
              <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
              <p className="font-medium truncate">
                {credentialsStatus.created_at ? new Date(credentialsStatus.created_at).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>

          {showDeleteConfirm ? (
            <Alert variant="destructive" className="bg-destructive/5 border-destructive/20">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{isGcp ? 'Disconnect GCP Bucket?' : 'Disconnect S3 Bucket?'}</AlertTitle>
              <AlertDescription className="mt-2 flex flex-col gap-3">
                <p>Are you sure you want to remove these credentials? You will need to re-enter them to transfer files.</p>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Yes, Disconnect
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditMode(true);
                  setBucketName(credentialsStatus.bucket_name || credentialsStatus.bucketName || '');
                  setPrefix(credentialsStatus.prefix || '');
                }}
                disabled={loading}
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Edit/Create Mode
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          {editMode ? 'Update Bucket Settings' : isGcp ? 'Connect GCP Bucket' : 'Connect S3 Bucket'}
        </CardTitle>
        <CardDescription>
          {editMode
            ? isGcp
              ? 'Change the bucket name or folder prefix. Your GCP credentials will remain unchanged.'
              : 'Change the bucket name or folder prefix. Your AWS credentials will remain unchanged.'
            : isGcp
              ? 'Enter your GCP HMAC credentials to browse and transfer files securely.'
              : 'Enter your AWS credentials to browse and transfer files securely.'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!editMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="accessKey">{isGcp ? 'HMAC Access Key' : 'AWS Access Key ID'} <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Key className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="accessKey"
                  placeholder={isGcp ? 'GOOG...' : 'AKIA...'}
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  className="pl-9"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secretKey">{isGcp ? 'HMAC Secret Key' : 'AWS Secret Access Key'} <span className="text-destructive">*</span></Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="secretKey"
                  type="password"
                  placeholder="Secret Key..."
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  className="pl-9"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bucketName">Bucket Name <span className="text-destructive">*</span></Label>
            <div className="relative">
              <Database className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="bucketName"
                placeholder="my-data-bucket"
                value={bucketName}
                onChange={(e) => setBucketName(e.target.value)}
                className="pl-9"
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prefix">Folder Prefix (Optional)</Label>
            <div className="relative">
              <FolderOpen className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="prefix"
                placeholder="data/exports/"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                className="pl-9"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="bg-muted/50 p-4 rounded-lg text-sm text-muted-foreground space-y-2">
          <p className="font-medium flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Security Note
          </p>
          <p>
            Credentials are encrypted via AWS Secrets Manager and never exposed to the browser.
            {isGcp
              ? ' Required GCP permissions: Storage Object Viewer role.'
              : ' Required permissions: s3:ListBucket and s3:GetObject.'
            }
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end gap-2 pb-6 pr-6">
        {editMode && (
          <Button
            variant="ghost"
            onClick={() => {
              setEditMode(false);
              setAccessKey('');
              setSecretKey('');
              setBucketName('');
              setPrefix('');
              setError(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {editMode ? 'Updating...' : 'Connecting...'}
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {editMode ? 'Update Settings' : 'Connect Bucket'}
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
